import { useCallback } from "react";
import { useRouter } from "expo-router";
import { useSetAtom } from "jotai/react";

import {
  useSignInWithAppleMutation,
  useSignInWithGoogleMutation,
} from "@/data/mutations/authMutations";
import {
  SSO_ERROR_POPUP_CLOSED,
  SSO_ERROR_REQUEST_CANCELED,
} from "@/domain/constants";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { useTranslator } from "@/hooks/useTranslate";
import { mfaResolverAtom } from "@/stores/atoms";
import { logger } from "@/utils/logger";
import { handleMfaError } from "@/utils/mfa";

export const useSSOAuth = (shortCode?: string) => {
  const router = useRouter();
  const { t } = useTranslator();
  const { isWeb } = usePlatformOS();
  const setMfaResolver = useSetAtom(mfaResolverAtom);

  const { mutateAsync: signInWithGoogle, isPending: isGoogleLoading } =
    useSignInWithGoogleMutation(shortCode);
  const { mutateAsync: signInWithApple, isPending: isAppleLoading } =
    useSignInWithAppleMutation(shortCode);

  const promptGoogleAuthAsync = useCallback(async () => {
    try {
      await signInWithGoogle();
    } catch (error: unknown) {
      const googleAuthError = error as { code?: string };
      if (
        googleAuthError.code === SSO_ERROR_REQUEST_CANCELED ||
        googleAuthError.code === SSO_ERROR_POPUP_CLOSED
      ) {
        return;
      }
      if (
        handleMfaError(error, resolver => {
          setMfaResolver(resolver);
          router.push("/(unauthed)/mfa-verify");
        })
      ) {
        return;
      }
      logger.error({
        message: t("native:authScreen.googleSignInFailed"),
        error,
      });
    }
  }, [signInWithGoogle, router, setMfaResolver, t]);

  // Handle Apple Sign-In
  const promptAppleAuthAsync = useCallback(async () => {
    try {
      await signInWithApple();
    } catch (error: unknown) {
      const appleAuthError = error as { code?: string };
      if (
        appleAuthError.code === SSO_ERROR_REQUEST_CANCELED ||
        appleAuthError.code === SSO_ERROR_POPUP_CLOSED
      ) {
        // User cancelled the sign-in, do not log as an error
        return;
      }
      if (
        handleMfaError(error, resolver => {
          setMfaResolver(resolver);
          router.push("/(unauthed)/mfa-verify");
        })
      ) {
        return;
      }
      logger.error({
        message: t("native:authScreen.appleSignInFailed"),
        error,
      });
    }
  }, [signInWithApple, router, setMfaResolver, t]);

  return {
    promptGoogleAuthAsync,
    isGoogleLoading,
    isGoogleAuthReady: isWeb,
    promptAppleAuthAsync,
    isAppleLoading,
  };
};
